import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { packagesApi } from "../api/packages";
import { qk } from "../api/keys";
import type { PackageInput } from "../api/types";

export function useClientPackages(clientId: number) {
  return useQuery({
    queryKey: qk.packages.forClient(clientId),
    queryFn: ({ signal }) => packagesApi.listForClient(clientId, signal),
  });
}

export function usePackageReminders() {
  return useQuery({
    queryKey: qk.packages.reminders,
    queryFn: ({ signal }) => packagesApi.reminders(signal),
  });
}

function invalidatePackages(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: qk.packages.root });
  void qc.invalidateQueries({ queryKey: qk.clients.root });
  void qc.invalidateQueries({ queryKey: qk.sessions.root });
}

export function useCreatePackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, body }: { clientId: number; body: PackageInput }) =>
      packagesApi.create(clientId, body),
    onSuccess: () => invalidatePackages(qc),
  });
}

export function useUpdatePackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ packageId, body }: { packageId: number; body: Partial<PackageInput> }) =>
      packagesApi.update(packageId, body),
    onSuccess: () => invalidatePackages(qc),
  });
}

export function useCancelPackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (packageId: number) => packagesApi.cancel(packageId),
    onSuccess: () => invalidatePackages(qc),
  });
}
